import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router';
import { Button, Icon } from '@iq/react-components';

import {
  createSite,
  lookupSite,
  uploadSitesExcelFile,
  getCreatedSite,
  getSites,
} from '../../bundles/sites';
import { getLayouts } from '../../bundles/layouts';
import config from '../../config';
import Loader from '../Loader';
import SiteCreateModal from './components/SiteCreateModal';
import SitesImportModal from './components/SitesImportModal';
import { DropdownMenu } from '../DropdownMenu';

const transformErrors = (errors) =>
  errors.map((error) => {
    switch (error.name) {
      case 'format': {
        if (error.property === '.org')
          error.message = 'Organization should only contain alpha-numeric characters (no spaces)';
        break;
      }
      case 'maximum':
      case 'minimum': {
        if (error.property === '.lat') error.message = 'Enter valid Latitude';
        else if (error.property === '.lng') error.message = 'Enter valid Longitude';
        break;
      }
      case 'required': {
        let property = error.property.includes('.') ? error.property.slice(1) : error.property;
        if (property.indexOf('location') === 0) {
          [, property] = property.split('.');
        }
        if (property === 'lat') error.message = 'Enter Latitude';
        else if (property === 'lng') error.message = 'Enter Longitude';
        else error.message = `Enter ${property.slice(0, 1).toUpperCase() + property.slice(1)}`;
        break;
      }
      default:
    }

    return error;
  });

const SiteCreateView = React.memo(({ siteParams: siteParameters = {}, realm }) => {
  const navigate = useNavigate();
  const { onPremise } = config;
  const { integrationInfo = {}, properties, thirdPartyId } = siteParameters;
  const { isIntegration = false, name: integrationName = '' } = integrationInfo;
  const [displayAddSiteModal, setDisplayAddSiteModal] = useState(isIntegration);

  const [displayImportSitesModal, setDisplayImportSitesModal] = useState(false);
  const [creatingSite, setCreatingSite] = useState(false);
  const [checkingExistingSite, setCheckingExistingSite] = useState(true);

  const dispatch = useDispatch();
  const createdSite = useSelector(getCreatedSite);
  const layouts = useSelector(getLayouts);
  const sites = useSelector(getSites);

  const layoutOptions = useMemo(
    () =>
      Object.keys(layouts)
        .filter((siteId) => sites.some((s) => s.id === siteId && s.isTemplate))
        .map((siteId) => ({
          key: siteId,
          siteName: sites.find(({ id }) => id === siteId)?.name,
        })),
    [sites, layouts]
  );

  const onCreate = useCallback(
    (data) => {
      setCreatingSite(true);
      const { template, ...siteData } = data;
      dispatch(
        createSite(
          {
            ...siteData,
            isIntegration,
            integrationName,
            org: realm,
            template,
            properties,
          },
          () => setCreatingSite(false)
        )
      );
      setDisplayAddSiteModal(false);
    },
    [dispatch, isIntegration, integrationName, realm, properties]
  );

  const onClose = useCallback(() => setDisplayAddSiteModal(false), []);

  const onImportSitesExcel = useCallback(
    (file) => {
      setDisplayImportSitesModal(false);
      dispatch(uploadSitesExcelFile(file, realm));
    },
    [dispatch, realm]
  );

  useEffect(() => {
    async function redirectIfSiteExists() {
      const type = integrationName.toLowerCase();

      if (thirdPartyId && type) {
        const data = await lookupSite(type, thirdPartyId, { relative: true });

        if (data?.exists) {
          navigate(data.url);
        }
      }

      setCheckingExistingSite(false);
    }

    redirectIfSiteExists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (createdSite && layouts?.[createdSite.id]) {
    return (
      <Navigate
        to={`/sites/${createdSite.id}`}
        replace
      />
    );
  }

  const renderAddSiteButton = () => {
    if (isIntegration) return null;

    if (onPremise) {
      return (
        <Button
          activity="primary"
          disabled={sites.length > 0}
          onClick={() => setDisplayAddSiteModal(true)}
        >
          Add site
        </Button>
      );
    }

    return (
      <DropdownMenu
        trigger={
          <Button
            icon={
              <Icon
                size="xs"
                icon="he-down"
              />
            }
            iconPosition="right"
          >
            Add site
          </Button>
        }
        triggerSize="s"
        menuOptions={[
          {
            component: <div>Add a site</div>,
            onSelect: () => setDisplayAddSiteModal(true),
          },
          {
            component: <div>Add sites in bulk</div>,
            onSelect: () => setDisplayImportSitesModal(true),
          },
        ]}
        className="site-item-menu"
        tooltip="Add site"
        tooltipPosition="bottom-start"
      />
    );
  };

  return (
    <div className="site-create-view-component">
      {checkingExistingSite ? (
        <Loader text="Checking for existing site ..." />
      ) : (
        <>
          {creatingSite ? (
            <Loader
              text="Creating site ..."
              overlay
            />
          ) : (
            renderAddSiteButton()
          )}
          {displayAddSiteModal && (
            <SiteCreateModal
              siteParams={siteParameters}
              layouts={layoutOptions}
              isOnPremise={onPremise}
              onSubmit={onCreate}
              transformErrors={transformErrors}
              onCloseModal={onClose}
            />
          )}
          {!onPremise && displayImportSitesModal && (
            <SitesImportModal
              layouts={layoutOptions}
              onSubmit={onImportSitesExcel}
              transformErrors={transformErrors}
              onCloseModal={() => setDisplayImportSitesModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
});

export default SiteCreateView;
